import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import type { PaymentMethodProvider } from '#src/__generated__/globalTypes.ts';
import { LoadingButton } from '#src/atoms/buttons/LoadingButton/index.tsx';
import { SBHorizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { CreditCard } from '#src/components/buyActions/CreditCard/index.tsx';
import { SettingsSection } from '#src/components/settings/SettingsSection/index.tsx';
import { Warning } from '#src/contexts/intl/Warning/index.tsx';

import { useCurrentUserPaymentMethods } from './useCurrentUserPaymentMethods.ts';
import { useDetachPaymentMethod } from './useDetachPaymentMethod.ts';

const messages = defineMessages({
  title: {
    defaultMessage: 'Credit Cards',
  },
  empty: {
    defaultMessage: 'No credit card saved.',
  },
  remove: {
    defaultMessage: 'Remove',
  },
  disabled: {
    defaultMessage: 'disabled',
  },
});

const Card = styled(SBHorizontal).attrs({ gap: 0 })`
  background-color: var(--c-nd-100);
  border-radius: 8px;
  padding: var(--double-unit);
  &:first-child {
    margin-top: 0;
  }
`;

const DetachPaymentMethodButton = ({
  id,
  provider,
}: {
  id: string;
  provider: PaymentMethodProvider;
}) => {
  const { detachPaymentMethod, loading } = useDetachPaymentMethod();
  return (
    <LoadingButton
      loading={loading}
      color="quaternary"
      size="small"
      onClick={() => {
        detachPaymentMethod(id, provider);
      }}
    >
      <FormattedMessage {...messages.remove} />
    </LoadingButton>
  );
};

export const PaymentMethods = () => {
  const paymentMethods = useCurrentUserPaymentMethods();
  return (
    <SettingsSection title={messages.title}>
      {paymentMethods.length === 0 && (
        <Warning variant="grey" title={messages.empty} />
      )}
      {paymentMethods?.length > 0 && (
        <Vertical gap={2}>
          {paymentMethods.map(paymentMethod => (
            <Card key={paymentMethod.id}>
              <CreditCard
                creditCard={paymentMethod.card}
                color={paymentMethod.disabled ? 'var(--c-nd-600)' : undefined}
                suffixText={
                  paymentMethod.disabled ? (
                    <>
                      (<FormattedMessage {...messages.disabled} />)
                    </>
                  ) : undefined
                }
              />
              <DetachPaymentMethodButton
                id={paymentMethod.id}
                provider={paymentMethod.provider}
              />
            </Card>
          ))}
        </Vertical>
      )}
    </SettingsSection>
  );
};
